import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import MultiSelect, { MultiSelectOption } from '../../shared/components/MultiSelect';
import Pagination from '../../shared/components/Pagination';
import {
  P2pOfferType,
  P2pRestriction,
  P2pRestrictionType,
  PaginatedResponse,
} from '../../shared/types';
import { CRYPTO_CURRENCIES, FIAT_CURRENCIES } from '../../shared/constants/markets';
import {
  RestrictionInput,
  createRestriction,
  deleteRestriction,
  listRestrictions,
  updateRestriction,
} from './restrictionsApi';
import { useAuth } from '../auth/AuthContext';

const TYPE_OPTIONS: { value: P2pRestrictionType; label: string; hint: string }[] = [
  { value: 'offer_create', label: 'Offer creation', hint: 'enables creating offers in the market' },
  { value: 'trade_view', label: 'Trade visibility', hint: 'enables seeing offers and opening trades' },
];

const TYPE_BADGE: Record<P2pRestrictionType, string> = {
  offer_create: 'badge-warning',
  trade_view: 'badge-info',
};

const TYPE_LABEL: Record<P2pRestrictionType, string> = {
  offer_create: 'Offer creation',
  trade_view: 'Trade visibility',
};

const VERIFICATION_LEVEL_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: '0 — No requirement' },
  { value: 1, label: '1 — Registered' },
  { value: 2, label: '2 — Email verified' },
  { value: 3, label: '3 — Phone verified' },
  { value: 4, label: '4 — Basic KYC' },
  { value: 5, label: '5 — Advanced KYC' },
];

const emptyForm: RestrictionInput = {
  name: '',
  description: '',
  type: 'offer_create',
  currencies: [],
  fiat_currencies: [],
  offer_type: null,
  min_verification_level: 0,
  is_active: true,
};

const CRYPTO_OPTIONS: MultiSelectOption[] = CRYPTO_CURRENCIES;
const FIAT_OPTIONS: MultiSelectOption[] = FIAT_CURRENCIES;

const joinList = (values: string[] | null | undefined): string | null => {
  if (!values || values.length === 0) return null;
  if (values.length <= 3) return values.join('/');
  return `${values.slice(0, 3).join('/')}+${values.length - 3}`;
};

const formatScope = (restriction: P2pRestriction): string => {
  const parts: string[] = [];
  const crypto = joinList(restriction.currencies);
  const fiat = joinList(restriction.fiat_currencies);
  if (crypto) parts.push(crypto);
  if (fiat) parts.push(fiat);
  if (restriction.offer_type) parts.push(restriction.offer_type.toUpperCase());
  return parts.length > 0 ? parts.join(' · ') : 'All markets';
};

// Market whitelists are kept per section (P2P / OTC cash trades).
type Props = { mode: 'p2p' | 'otc' };

const Restrictions: React.FC<Props> = ({ mode }) => {
  const isOtc = mode === 'otc';
  const { admin } = useAuth();
  const [restrictions, setRestrictions] = useState<PaginatedResponse<P2pRestriction> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | P2pRestrictionType>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<P2pRestriction | null>(null);
  const [formData, setFormData] = useState<RestrictionInput>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const canManage = useMemo(() => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    if (admin.backoffice_role === 'administrator') return true;
    return admin.permissions?.includes('p2p.manage_restrictions') ?? false;
  }, [admin]);

  const load = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listRestrictions({
        page,
        per_page: 20,
        mode,
        search: search || undefined,
        type: typeFilter || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
      });
      setRestrictions(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load restrictions'
        : 'Failed to load restrictions';
      setError(msg);
      setRestrictions(null);
    } finally {
      setIsLoading(false);
    }
  }, [search, typeFilter, activeFilter, mode]);

  useEffect(() => {
    load(currentPage);
  }, [currentPage, load]);

  const applyFilters = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setActionError('');
    setModalOpen(true);
  };

  const openEdit = (restriction: P2pRestriction) => {
    setEditing(restriction);
    setFormData({
      name: restriction.name,
      description: restriction.description ?? '',
      type: restriction.type,
      currencies: restriction.currencies ?? [],
      fiat_currencies: restriction.fiat_currencies ?? [],
      offer_type: restriction.offer_type,
      min_verification_level: restriction.min_verification_level,
      is_active: restriction.is_active,
    });
    setActionError('');
    setModalOpen(true);
  };

  const buildPayload = (): RestrictionInput => ({
    mode,
    name: formData.name.trim(),
    description: formData.description?.trim() ? formData.description.trim() : null,
    type: formData.type,
    currencies: formData.currencies ?? [],
    fiat_currencies: formData.fiat_currencies ?? [],
    offer_type: formData.offer_type ?? null,
    min_verification_level: Number(formData.min_verification_level ?? 0),
    is_active: formData.is_active ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError('');
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await updateRestriction(editing.id, payload);
      } else {
        await createRestriction(payload);
      }
      setModalOpen(false);
      setEditing(null);
      load(currentPage);
    } catch (err: unknown) {
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string; errors?: Record<string, string[]> } } }).response
        : undefined;
      const errors = resp?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        setActionError(Array.isArray(first) ? first[0] : 'Validation failed');
      } else {
        setActionError(resp?.data?.error || resp?.data?.message || 'Failed to save');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (restriction: P2pRestriction) => {
    if (!window.confirm(`Delete restriction "${restriction.name}"?`)) return;
    try {
      await deleteRestriction(restriction.id);
      load(currentPage);
    } catch {
      window.alert('Failed to delete');
    }
  };

  const handleToggleActive = async (restriction: P2pRestriction) => {
    try {
      await updateRestriction(restriction.id, {
        name: restriction.name,
        type: restriction.type,
        min_verification_level: restriction.min_verification_level,
        is_active: !restriction.is_active,
      });
      load(currentPage);
    } catch {
      window.alert('Failed to update status');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{isOtc ? 'OTC' : 'P2P'} Restrictions</h1>
            <p className="text-slate-400 mt-1">
              Whitelist of enabled {isOtc ? 'OTC (cash)' : 'P2P'} markets{isOtc ? ' — independent from P2P' : ''}: a user can create offers (or see offers and
              open trades) only in markets covered by an active rule whose verification level
              they meet. A market with no matching rule is disabled. A rule without fiat
              exposes only the default fiat (INR) in the offer creation form.
            </p>
          </div>
          {canManage && (
            <button onClick={openCreate} className="btn btn-primary">
              + Add Restriction
            </button>
          )}
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyFilters())}
              placeholder="Name or description"
              className="input py-2 w-64"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value as '' | P2pRestrictionType); setCurrentPage(1); }}
              className="input py-2"
            >
              <option value="">All types</option>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value as '' | 'true' | 'false'); setCurrentPage(1); }}
              className="input py-2"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <button type="button" onClick={applyFilters} className="btn btn-secondary py-2">Apply</button>
          {(search || typeFilter || activeFilter) && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); setTypeFilter(''); setActiveFilter(''); setCurrentPage(1); }}
              className="btn btn-secondary py-2"
            >
              Clear
            </button>
          )}
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !restrictions?.data.length ? (
            <div className="p-12 text-center text-slate-400">
              No restrictions configured. {canManage && 'Click "+ Add Restriction" to create one.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Scope</th>
                      <th className="table-header">Min. verification</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Updated</th>
                      {canManage && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {restrictions.data.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{r.id}</td>
                        <td className="table-cell text-white">
                          <div className="font-medium">{r.name}</div>
                          {r.description && (
                            <div className="text-xs text-slate-500 max-w-xs truncate">{r.description}</div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${TYPE_BADGE[r.type]}`}>
                            {TYPE_LABEL[r.type]}
                          </span>
                        </td>
                        <td className="table-cell text-slate-400 text-xs">{formatScope(r)}</td>
                        <td className="table-cell text-slate-200">
                          {VERIFICATION_LEVEL_OPTIONS.find((o) => o.value === r.min_verification_level)?.label
                            ?? `Level ${r.min_verification_level}`}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${r.is_active ? 'badge-success' : 'badge-warning'}`}>
                            {r.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="table-cell text-slate-400 text-xs">
                          {new Date(r.updated_at).toLocaleString()}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleToggleActive(r)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-700/40 hover:bg-slate-700 rounded-lg transition-all"
                              >
                                {r.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => openEdit(r)}
                                className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(r)}
                                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {restrictions.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={restrictions.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        title={editing ? `Edit restriction #${editing.id}` : 'Add restriction'}
        size="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="input"
                placeholder="e.g. USDT/USD offers require Basic KYC"
                required
                maxLength={191}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="input min-h-[60px]"
                maxLength={2000}
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Restriction type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData((p) => ({ ...p, type: e.target.value as P2pRestrictionType }))}
                className="input"
                required
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Minimum verification level *</label>
              <select
                value={formData.min_verification_level}
                onChange={(e) => setFormData((p) => ({ ...p, min_verification_level: Number(e.target.value) }))}
                className="input"
                required
              >
                {VERIFICATION_LEVEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 mt-2 pt-2 border-t border-slate-800/60">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Scope (leave empty = applies to all)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Crypto currency</label>
              <MultiSelect
                options={CRYPTO_OPTIONS}
                value={formData.currencies ?? []}
                onChange={(vals) => setFormData((p) => ({ ...p, currencies: vals }))}
                placeholder="All crypto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Fiat currency</label>
              <MultiSelect
                options={FIAT_OPTIONS}
                value={formData.fiat_currencies ?? []}
                onChange={(vals) => setFormData((p) => ({ ...p, fiat_currencies: vals }))}
                placeholder="All fiat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Offer type</label>
              <select
                value={formData.offer_type ?? ''}
                onChange={(e) => setFormData((p) => ({
                  ...p,
                  offer_type: (e.target.value || null) as P2pOfferType | null,
                }))}
                className="input"
              >
                <option value="">Both</option>
                <option value="buy">Buy only</option>
                <option value="sell">Sell only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                value={formData.is_active ? '1' : '0'}
                onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.value === '1' }))}
                className="input"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-800/60">
            <button
              type="button"
              onClick={() => { setModalOpen(false); setEditing(null); }}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default Restrictions;
